import * as jsPDF from 'jspdf';
import 'jspdf-autotable';


/**
 * @typedef TableOptions
 * @property {Array<Array<String>>} head The header data, which will make up the column headers.
 * @property {Array<Array<String>>} body The row data.
 * @property {Boolean} showHead Whether or not to show the table header.
 * @property {String} theme Table theme.
 * @property {Number} extendCursor The amount to extend the pdf cursor.
 * @property {String} fontStyle For example, 'normal', 'bold', 'italic'.
 * @property {Number} fontSize The size of the font, in units chosen.
 * @property {String} fillColor Color in hex.
 * @property {String} textColor Color in hex.
 * @property {Number} cellPadding Table cell padding, in units chosen.
 */

/**
 * Encapsulates the functionality of generating PDFs on the client side.
 */
export class PDFWriter {
    constructor(jsPDFClass = jsPDF) {
        this.pdf = this.initPDF(jsPDFClass);
        this.defaultTableHeaderRowHeight = 10;
        this.defaultTableRowHeight = 10;
        this.currentCursorLocation = 10;
        this.margin = 10;
    }

    /**
     * Initialize jsPDF
     * @param {jsPDF} jsPDF
     * @returns {jsPDF.jsPDF} Returns an instance of jsPDF, a PDF data object.
     */
    initPDF(jsPDF) {
        return new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'letter',
        });
    }

    /**
     * Manipulates the position of the active cursor.
     * @param {Number} [space=10] The amount of space to extend the cursor from the last location. (Default unit mm).
     */
    extendCursor(space = 10) {
        this.currentCursorLocation += space;
    }

    /**
     * 
     * @param {TableOptions} options 
     */
    calculateTableSize(options) {
        const { body, head, showHead } = options;
        if(Array.isArray(body)) {
            const tableHeaderRowHeight = (head && showHead) ? this.defaultTableHeaderRowHeight : 0;
            return tableHeaderRowHeight + (body.length * this.defaultTableRowHeight);
        }
    }

    /**
     * Adds a new page to the pdf, advancing the cursor to the beginning of the new page.
     */
    addPage() {
        this.currentCursorLocation = 10;
        this.pdf.addPage();
    }

    /**
     * Writes a PDF header row
     * @param {String} title 
     * @param {TableOptions} options 
     */
    writeHeader(title, options = {}) {
        this.writeTable({
            body: [[title]],
            fontSize: 14,
            extendCursor: options.extendCursor,
            fillColor: options.fillColor || '#F4F5F7',
            fontStyle: options.fontStyle || 'bold',
            ...options,
        });
    }

    /**
     * Writes a PDF table with a variety of options
     * @param {TableOptions} options 
     */
    writeTable(options) {
        this.pdf.autoTable({ 
            startY: this.currentCursorLocation,
            margin: { horizontal: this.margin},
            head: options.head || null,
            body: options.body || null,
            showHead: options.showHead,
            theme: options.theme || 'plain',
            headStyles: {fontSize: 10},
            styles: {
                fontStyle: options.fontStyle,
                fillColor: options.fillColor,
                textColor: options.textColor || 'black',
                fontSize:  options.fontSize || 9,
                cellPadding: options.cellPadding || 2,
            },
        });
        const tableSize = options.extendCursor || this.calculateTableSize(options);
        this.extendCursor(tableSize);
    }

    /**
     * Writes a horizontal line at the current cursor location.
     * @param {Number} width Width of the horizontal line.
     */
    writeHorizontalLine(width) {
        this.pdf.setLineWidth(width);
        const rightMargin = this.pdf.internal.pageSize.getWidth() - this.margin;
        this.pdf.line(this.margin, this.currentCursorLocation, rightMargin, this.currentCursorLocation);
        this.extendCursor(5);
    }

    /**
     * Tags each page heading with a page count and export date.
     * @param {String} exportDate Formatted date only string.
     */
    writePageHeaders(exportDate) {
        this.pdf.setFontSize(8);
        const pageNumber = this.pdf.internal.getNumberOfPages();
        const xCoordinate = 230;
        const yCoordinate = 5;
        for (let currentPage = 1; currentPage <= pageNumber; currentPage++) {
            this.pdf.setPage(currentPage);
            this.pdf.text(`Page ${currentPage} of ${pageNumber} | Generated ${exportDate}`, xCoordinate, yCoordinate);
        }
    }

    /**
     * Saves generated PDF to client.
     * @param {String} fileName Name of PDF to save
     */
    savePDF(fileName) {
        this.pdf.save(fileName);
    }
}